import { presignedUrl, uploadToPresignedUrl } from "@/api/storage.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation } from "@tanstack/react-query";
import { Upload } from "lucide-react";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Icons } from "../ui/icons";

interface ImageInputProps {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  form: UseFormReturn<any>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  field: any;
  contentType: string;
  displayType?: "card";
}

const CardDisplay: React.FC<{
  succeededUrl: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
  setSucceededUrl: React.Dispatch<React.SetStateAction<string | null>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  form: UseFormReturn<any>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  field: any;
  isUploading: boolean;
}> = ({
  succeededUrl,
  fileInputRef,
  setFile,
  setSucceededUrl,
  form,
  field,
  isUploading,
}) => {
  return (
    <div className="grid gap-2">
      {succeededUrl ? (
        <div>
          <img
            src={succeededUrl}
            alt="Uploaded"
            className="aspect-square w-full rounded-md object-cover"
            style={{ maxWidth: "200px", maxHeight: "200px" }}
          />
          <Button
            variant={"outline"}
            onClick={(e) => {
              e.preventDefault();
              setFile(null);
              setSucceededUrl(null);
              form.resetField(field.name);

              // Call the onChange handler with empty string if provided
              if (field.onChange) {
                field.onChange("");
              }
            }}
          >
            Remove
          </Button>
        </div>
      ) : (
        <button
          className="flex aspect-square w-full items-center justify-center rounded-md border border-dashed"
          style={{ maxWidth: "200px", height: "200px" }}
          onClick={(e) => {
            e.preventDefault();
            fileInputRef.current?.click();
          }}
        >
          {!isUploading ? (
            <Upload className="h-4 w-4 text-muted-foreground" />
          ) : null}
          {isUploading ? (
            <Icons.spinner className="h-4 w-4 animate-spin" />
          ) : null}
          <span className="sr-only">Upload</span>
        </button>
      )}
    </div>
  );
};

const DefaultDisplay: React.FC<{
  succeededUrl: string | null;
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
  setSucceededUrl: React.Dispatch<React.SetStateAction<string | null>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  form: UseFormReturn<any>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  field: any;
  isUploading: boolean;
}> = ({ succeededUrl, setFile, setSucceededUrl, form, field, isUploading }) => {
  return (
    <div className="grid grid-cols-2 gap-4 mt-2">
      {isUploading ? <Icons.spinner className="h-4 w-4 animate-spin" /> : null}
      {succeededUrl ? (
        <div>
          <img
            src={succeededUrl}
            alt="Uploaded"
            style={{ maxWidth: "100px", maxHeight: "100px" }}
          />
        </div>
      ) : null}
      {succeededUrl ? (
        <Button
          variant={"outline"}
          onClick={(e) => {
            e.preventDefault();
            setFile(null);
            setSucceededUrl(null);
            form.resetField(field.name);

            // Call the onChange handler with empty string if provided
            if (field.onChange) {
              field.onChange("");
            }
          }}
        >
          Remove
        </Button>
      ) : null}
    </div>
  );
};

export const ImageInput: React.FC<ImageInputProps> = ({
  form,
  field,
  contentType,
  displayType,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Track the last uploaded URL to prevent duplicate changes
  const lastUploadedUrlRef = useRef<string>("");

  const { mutateAsync: getPresignedUrl } = useMutation({
    mutationFn: presignedUrl,
  });

  const [succeededUrl, setSucceededUrl] = useState<string | null>(
    form.getValues(field.name),
  );

  const { mutateAsync: uploadFile } = useMutation({
    mutationFn: (args: { url: string; file: File }) =>
      uploadToPresignedUrl(args.url, args.file),
  });

  // Update the succeededUrl when the form value changes externally
  useEffect(() => {
    const formValue = form.getValues(field.name);
    if (formValue && formValue !== succeededUrl) {
      setSucceededUrl(formValue);
    }
  }, [form, field.name, succeededUrl]);

  useEffect(() => {
    // Only process if we have a file and we're not already uploading
    if (file && !isUploading) {
      setIsUploading(true);

      const specificContentType = file.type || contentType;

      getPresignedUrl({
        contentType: specificContentType,
        extension: file.name.split(".").pop() || "",
        persist: true,
      })
        .then((data) => {
          if (data?.presigned) {
            return uploadFile({ url: data.presigned, file }).then(() => {
              const urlWithoutQuery = data.presigned.split("?")[0];

              // Don't update if this is the same URL we just uploaded
              if (urlWithoutQuery === lastUploadedUrlRef.current) {
                return;
              }

              // Update our reference to the last URL
              lastUploadedUrlRef.current = urlWithoutQuery;

              // Update the local state
              setSucceededUrl(urlWithoutQuery);

              // Update the form value
              form.setValue(field.name, urlWithoutQuery, {
                shouldDirty: true,
                shouldTouch: true,
              });

              // Call the onChange handler once, if provided
              if (field.onChange) {
                field.onChange(urlWithoutQuery);
              }
            });
          }
        })
        .catch((error) => {
          console.error("Upload error:", error);
        })
        .finally(() => {
          // Clear the file state to prevent re-uploads
          setFile(null);
          setIsUploading(false);
        });
    }
  }, [
    file,
    contentType,
    getPresignedUrl,
    uploadFile,
    form,
    field,
    isUploading,
  ]);

  return (
    <div>
      {displayType === "card" ? (
        <CardDisplay
          succeededUrl={succeededUrl}
          fileInputRef={fileInputRef}
          setFile={setFile}
          setSucceededUrl={setSucceededUrl}
          form={form}
          field={field}
          isUploading={isUploading}
        />
      ) : (
        <DefaultDisplay
          succeededUrl={succeededUrl}
          setFile={setFile}
          setSucceededUrl={setSucceededUrl}
          form={form}
          field={field}
          isUploading={isUploading}
        />
      )}

      <Input
        className={displayType === "card" ? "hidden" : ""}
        ref={fileInputRef}
        type="file"
        hidden={displayType === "card"}
        accept={contentType || "image/*"}
        onChange={(e) => {
          const files = e.target.files;
          if (files?.[0]) {
            setFile(files[0]);
          }
        }}
      />
    </div>
  );
};
